import { FC, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useConditionalEffect } from 'react-delta';
import { getLinkForRoute } from 'common/util/location';
import { ROUTE, URL_PARAM } from 'common/constants';
import { UrlParams } from 'common/types';
import { Column, PqResultData, QueryType } from 'common/graphqlTypes';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';

import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import { generatePQRequest } from 'hooks/useLRQ/useLRQ';

import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { eventParamsState, queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import { PQDataValue } from 'common/util/pqUtil';
import { getRequestId } from 'common/util/lrqRequest';
import PowerQueryResultTable from 'components/common/PowerQueryResultTable/PowerQueryResultTable';
import { MONITORS_FILTER } from './consts';

const URL_INDEX = 0;
const LABEL_INDEX = 2;

const buildSearchLink = (name: string, url: string, eventParams: UrlParams): string => {
  switch (true) {
    case name === 'URL (Label)':
      return getLinkForRoute(ROUTE.EVENTS, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
      } as UrlParams);
    case name === 'Count':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
      } as UrlParams);
    case name === '2xx':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
      } as UrlParams);
    case name === '3xx':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor' status >= 300 status <= 399) $url="${url}"`,
      } as UrlParams);
    case name === '4xx':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor' status >= 400 status <= 499) $url="${url}"`,
      } as UrlParams);
    case name === '5xx':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor' status >= 500 status <= 599) $url="${url}"`,
      } as UrlParams);
    case name === 'Size':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
        [URL_PARAM.FACET]: 'responseSize',
      } as UrlParams);
    case name === 'MaxTime':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
        [URL_PARAM.FACET]: 'time',
      } as UrlParams);
    case name === 'AverageTime':
      return getLinkForRoute(ROUTE.GRAPH, eventParams, {
        [URL_PARAM.FILTER]: `(tag='httpMonitor') $url="${url}"`,
        [URL_PARAM.FACET]: 'time',
      } as UrlParams);
    default:
      return getLinkForRoute(ROUTE.MONITORS_PAGE, eventParams);
  }
};

const buildRowTitle = (url: string, label: string): string => {
  if (label) {
    return label;
  }

  if (url) {
    return url;
  }

  return '';
};

const buildPowerQueryTableHeaders = (columns: PqResultData['columns']): Column[] =>
  columns
    .filter((column, i) => i !== LABEL_INDEX)
    .map(({ name, format, decimalPlaces }, i) => {
      const modifiedName = i === URL_INDEX ? 'Label (URL)' : name;

      return {
        name: modifiedName,
        format,
        decimalPlaces,
      } as Column;
    });

const buildPowerQueryTableRowValues = (
  values: PqResultData['values'],
  columns: PqResultData['columns'],
  eventParams: UrlParams,
): PQDataValue[][] =>
  values
    .filter((row) => row[URL_INDEX] || row[LABEL_INDEX])
    .map((row, rowIndex) =>
      row
        .filter((value, i) => i !== LABEL_INDEX)
        .map((value, i) => {
          if (i === URL_INDEX) {
            const label = values[rowIndex][LABEL_INDEX];

            return buildRowTitle(value, label);
          }

          const header = buildPowerQueryTableHeaders(columns)[i];

          return {
            url: buildSearchLink(header.name, row[URL_INDEX], eventParams),
            value,
          };
        }),
    );

const MonitorsContent: FC = () => {
  const isNewSearch = useIsNewSearch();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, requestTime, endTime } = queryTimeRange;
  const eventParams = useRecoilValue(eventParamsState);
  const requestFromUrl = generatePQRequest({
    endTime,
    filter: MONITORS_FILTER,
    requestTime,
    startTime,
  });
  const [request, setRequest] = useState(requestFromUrl);

  useConditionalEffect(() => {
    setRequest(requestFromUrl);
  }, isNewSearch);

  const {
    results: [
      {
        isDone,
        pqResultData: { columns = [], values = [] },
      },
    ],
  } = useEventCache([getRequestId(request, QueryType.PQ)]);

  return (
    <>
      {isDone ? (
        <PowerQueryResultTable
          headers={buildPowerQueryTableHeaders(columns)}
          isNonVirtual
          rowValues={buildPowerQueryTableRowValues(values, columns, eventParams)}
        />
      ) : (
        <LoadingPlaceholder />
      )}
      {MONITORS_FILTER && <LRQRequestManager showProgress key={request.key} request={request} />}
    </>
  );
};

export default MonitorsContent;
